import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { systemSettingsApi } from '~/apis/systemSettingsApi'
import { useUserState } from '~/atoms/UserState'
import { useCheckRole } from '~/hooks/useCheckRole'

import { normalizeTimeZone } from '@mangovoice/constants'
import { useGetSystemSettings } from '@mangovoice/system-settings-ui'

interface DashboardContextValue {
  pbxId: number
  timeZone?: string
  callCenterEnabled: boolean
  isAdmin: boolean
  isLoading: boolean
}

const DashboardContext = createContext<DashboardContextValue | null>(null)

export const useDashboardContext = () => {
  const ctx = useContext(DashboardContext)
  if (!ctx) {
    throw new Error('useDashboardContext must be used within DashboardProvider')
  }
  return ctx
}

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const { agentStatusesEnabled, pbxId: userPbxId, userRoles } = useUserState()
  const checkRole = useCheckRole()

  const validPbxId = userPbxId || 0
  const { data: systemSettings } = useGetSystemSettings(
    systemSettingsApi,
    validPbxId || undefined,
  )
  const timeZone = systemSettings?.default_time_zone
    ? normalizeTimeZone(systemSettings.default_time_zone)
    : undefined

  const isLoading = userRoles === undefined
  const isAdmin = checkRole('admin')
  const callCenterEnabled = !!agentStatusesEnabled && isAdmin

  const value = useMemo<DashboardContextValue>(
    () => ({
      pbxId: validPbxId,
      timeZone,
      callCenterEnabled,
      isAdmin,
      isLoading,
    }),
    [validPbxId, timeZone, callCenterEnabled, isAdmin, isLoading],
  )

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  )
}
